/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useEffect, useState } from 'react';
import { useRootSelector } from '../../../../../hooks/useRootSelector';

enum ScrollDirections {
  neutral = 'neutral',
  up = 'up',
  down = 'down',
}

const MINIMUM_SCROLL_BEFORE_HIDE_MOBILE_DOC_SUBHEADER = -10;
const MINIMUM_SCROLL_BEFORE_SHOW_MOBILE_DOC_SUBHEADER = 40;

export const useDocumentViewer = (shouldShowSigningIframe: boolean) => {
  const { isMobileView, isMobileDevice, orientation, selectedDocument } = useRootSelector(state => ({
    isMobileDevice: state.layout.isMobileDevice,
    isMobileView: state.layout.isMobileView,
    orientation: state.layout.orientation,
    selectedDocument: state.document.selectedDoc,
  }));

  const [yPos, setYPos] = useState(0);
  const [lastYPos, setLastYPos] = useState(0);
  const [dominantScrollDirection, setDominantScrollDirection] = useState<ScrollDirections>(ScrollDirections.neutral);
  const [isMobileSubheaderHidden, setIsMobileSubheaderHidden] = useState(false);
  const [previousMobileOrientation, setPreviousMobileOrientation] = useState(orientation);
  const [hasRotatedAndNotMoved, setHasRotatedAndNotMoved] = useState(false);

  useEffect(() => {
    // If we rotated the phone, reset the mobile sub header
    if (isMobileView && previousMobileOrientation !== orientation) {
      setIsMobileSubheaderHidden(false);
      setPreviousMobileOrientation(orientation);
      setYPos(lastYPos);
      // setYPos(0);
      setDominantScrollDirection(ScrollDirections.neutral);
      setHasRotatedAndNotMoved(true);
    }
  }, [previousMobileOrientation, orientation, isMobileView, lastYPos]);

  const onScroll = (newYPos: number) => {
    setLastYPos(newYPos);
    // If we haven't moved, do nothing
    if (newYPos === yPos) {
      return;
    }
    // If we're moving in the same direction, update the yPos
    const newDirection: ScrollDirections = yPos < newYPos ? ScrollDirections.down : ScrollDirections.up;
    if (newDirection === dominantScrollDirection) {
      setYPos(newYPos);
    } else {
      if (newDirection === ScrollDirections.up && newYPos < yPos + MINIMUM_SCROLL_BEFORE_HIDE_MOBILE_DOC_SUBHEADER) {
        // If we've scrolled up 10px, show it
        setIsMobileSubheaderHidden(false);
        setYPos(newYPos);
        setDominantScrollDirection(ScrollDirections.up);
      } else if (
        newDirection === ScrollDirections.down &&
        newYPos > yPos + MINIMUM_SCROLL_BEFORE_SHOW_MOBILE_DOC_SUBHEADER
      ) {
        // If we've scrolled down 40px, hide it
        setIsMobileSubheaderHidden(true);
        setYPos(newYPos);
        setDominantScrollDirection(ScrollDirections.down);
      } else if (
        !isMobileSubheaderHidden &&
        dominantScrollDirection === ScrollDirections.neutral &&
        hasRotatedAndNotMoved
      ) {
        // If we've rotated the phone and scrolled, set the dominant scroll
        setHasRotatedAndNotMoved(false);
        setYPos(newYPos);
      }
    }
  };

  const shouldShowMobileDocSubheader = isMobileDevice && !shouldShowSigningIframe && !!selectedDocument;

  return {
    isMobileView,
    isMobileSubheaderHidden,
    shouldShowMobileDocSubheader,
    onScroll,
  };
};
